import { PHASES } from '../../../../../packages/contracts/src/entities/index.js';
import { presenterBlocage } from './blocages.js';
import { presenterDelai } from './delais.js';
import { resumeParcours, type EtapeAffichee } from './parcours.js';
import type { DossierVue, Etape } from './types.js';

/**
 * Affichage progressif de D03 (PARITE-2) : où en est le dossier, ce qui manque pour avancer. Aucune règle d'étape n'est
 * écrite ici : le parcours est une donnée du dossier (`steps`), les blocs propres à une étape viennent du référentiel.
 */
export interface RepartitionParcours {
  /** L'étape en cours du dossier (ou, à défaut, la prochaine à faire, ou la dernière). */
  courante: EtapeAffichee | null;
  /** L'étape regardée : celle choisie dans la frise, sinon la courante. */
  consultee: EtapeAffichee | null;
  /** Vrai quand l'étape regardée est l'étape en cours. */
  enCours: boolean;
  passees: EtapeAffichee[];
  futures: EtapeAffichee[];
}

export function repartirParcours(etapes: readonly Etape[], choisie?: string | null): RepartitionParcours {
  const parcours = resumeParcours(etapes);
  const rang = (etat: EtapeAffichee['etat']): number => parcours.findIndex((e) => e.etat === etat);
  const iCourante = rang('en_cours') >= 0 ? rang('en_cours') : rang('suivante') >= 0 ? rang('suivante') : parcours.length - 1;
  const iChoisie = choisie ? parcours.findIndex((e) => e.id === choisie) : -1;
  const iConsultee = iChoisie >= 0 ? iChoisie : iCourante;
  return {
    courante: parcours[iCourante] ?? null,
    consultee: parcours[iConsultee] ?? null,
    enCours: iConsultee === iCourante,
    passees: parcours.slice(0, Math.max(0, iConsultee)),
    futures: parcours.slice(iConsultee + 1),
  };
}

const rangPhase = (code: string): number => PHASES.findIndex((p) => p.code === code);

export type PlaceBloc = 'ici' | 'passe' | 'avenir';

/** Où se range un bloc propre à l'étape `phaseDuBloc` quand on regarde l'étape `phaseConsultee` (ordre P0 à P9). */
export function blocPlace(phaseDuBloc: string, phaseConsultee: string | null): PlaceBloc {
  if (phaseConsultee === null) return 'avenir';
  const bloc = rangPhase(phaseDuBloc);
  const vue = rangPhase(phaseConsultee);
  return bloc === vue ? 'ici' : bloc < vue ? 'passe' : 'avenir';
}

export const MANDAT_LIBELLE: Readonly<Record<DossierVue['mandateStatus'], string>> = {
  absent: 'à signer',
  brouillon: 'en préparation',
  envoye: 'envoyé, en attente de signature',
  signe: 'signé',
  refuse: 'refusé',
  revoque: 'révoqué',
};

export interface Manque {
  code: 'blocage' | 'mandat' | 'action' | 'delai';
  texte: string;
}

/** Ce qui manque pour avancer, tiré des données du dossier : blocage, mandat non signé, action suivante, délais à tenir. */
export function ceQuiManque(dossier: DossierVue, aujourdhui: string): Manque[] {
  const lignes: Manque[] = [];
  if (dossier.blocker) lignes.push({ code: 'blocage', texte: presenterBlocage(dossier.blocker).libelle });
  if (dossier.mandateStatus !== 'signe') {
    lignes.push({ code: 'mandat', texte: `Mandat de recherche : ${MANDAT_LIBELLE[dossier.mandateStatus]}` });
  }
  if (dossier.nextAction) lignes.push({ code: 'action', texte: dossier.nextAction });
  for (const d of dossier.deadlines) {
    const delai = presenterDelai(d, aujourdhui);
    if (delai.ton !== 'termine') lignes.push({ code: 'delai', texte: `${d.label} : ${delai.texte}` });
  }
  return lignes;
}
